/**
 * Lava: vanilla flowing-fluid rules, simplified like water's (world/fluid.ts).
 *
 *  - updates run 30 ticks (overworld) / 10 ticks (nether) after a disturbance
 *  - level drops by 2 per block in the overworld (reach 4), 1 in the nether (reach 8)
 *  - flows down first (falling lava), else sideways to open cells
 *  - flowing lava drains when its feed is gone (no infinite lava)
 *  - water contact: source -> obsidian, flowing -> cobblestone; lava falling
 *    onto water turns that water to stone
 *  - lava now and then sets fire to air next to flammable blocks nearby
 *
 * DOM-free (smoke-tested).
 */

import {
  AIR, COBBLE_ID, FALLING_LAVA_ID, FIRE_ID, FLOWING_LAVA_IDS, IS_FLAMMABLE, IS_LAVA, IS_SOLID, LAVA_AMOUNT,
  LAVA_ID, OBSIDIAN_ID, STONE_ID, WATER_AMOUNT,
} from '../blocks';

export interface LavaHost {
  getBlock(x: number, y: number, z: number): number;
  setBlock(x: number, y: number, z: number, id: number): void;
  canEdit(x: number, y: number, z: number): boolean;
  /** Ultrawarm dimension (nether): faster, farther lava. */
  nether(): boolean;
  /** A new fire block was lit (FireSim.ignite). */
  ignite?(x: number, y: number, z: number): void;
  rnd(): number;
}

const DX = [1, -1, 0, 0];
const DZ = [0, 0, 1, -1];

export const lavaDelay = (nether: boolean): number => (nether ? 10 : 30);
export const lavaDrop = (nether: boolean): number => (nether ? 1 : 2);

/** Can lava flow into this cell (air, flora, fire — not solids, liquids). */
const open = (id: number): boolean => IS_SOLID[id] === 0 && IS_LAVA[id] === 0 && WATER_AMOUNT[id] === 0;

export class LavaSim {
  private now = 0;
  private readonly due = new Map<string, number>();

  constructor(private readonly host: LavaHost) {}

  get pending(): number { return this.due.size; }

  /** A block changed: wake lava at and around it. */
  blockChanged(x: number, y: number, z: number): void {
    this.wake(x, y, z);
    this.wake(x, y + 1, z);
    this.wake(x, y - 1, z);
    for (let d = 0; d < 4; d++) this.wake(x + DX[d], y, z + DZ[d]);
  }

  private wake(x: number, y: number, z: number): void {
    if (IS_LAVA[this.host.getBlock(x, y, z)] !== 1) return;
    const k = x + ',' + y + ',' + z;
    if (!this.due.has(k)) this.due.set(k, this.now + lavaDelay(this.host.nether()));
  }

  tick(): void {
    this.now++;
    if (this.due.size === 0) return;
    const ready: string[] = [];
    for (const [k, t] of this.due) if (t <= this.now) ready.push(k);
    for (const k of ready) {
      this.due.delete(k);
      const [x, y, z] = k.split(',').map(Number);
      if (this.host.canEdit(x, y, z)) this.update(x, y, z);
      else this.due.set(k, this.now + 20); // chunk not ready: retry later
    }
  }

  private set(x: number, y: number, z: number, id: number): void {
    if (this.host.canEdit(x, y, z)) this.host.setBlock(x, y, z, id);
  }

  update(x: number, y: number, z: number): void {
    const h = this.host;
    let id = h.getBlock(x, y, z);
    if (IS_LAVA[id] !== 1) return;
    const nether = h.nether();
    const drop = lavaDrop(nether);

    // water contact hardens it (vanilla LiquidBlock.shouldSpreadLiquid)
    const touchesWater = WATER_AMOUNT[h.getBlock(x, y + 1, z)] > 0
      || DX.some((dx, d) => WATER_AMOUNT[h.getBlock(x + dx, y, z + DZ[d])] > 0);
    if (touchesWater) {
      this.set(x, y, z, id === LAVA_ID ? OBSIDIAN_ID : COBBLE_ID);
      return;
    }

    // recompute a flowing cell from its feed
    if (id !== LAVA_ID) {
      let want = 0;
      let wantId = AIR;
      if (IS_LAVA[h.getBlock(x, y + 1, z)] === 1) { want = 8; wantId = FALLING_LAVA_ID; }
      else {
        let best = 0;
        for (let d = 0; d < 4; d++) {
          const n = h.getBlock(x + DX[d], y, z + DZ[d]);
          if (IS_LAVA[n] !== 1) continue;
          // a falling column only feeds sideways where it lands on something
          if (n === FALLING_LAVA_ID && open(h.getBlock(x + DX[d], y - 1, z + DZ[d]))) continue;
          best = Math.max(best, LAVA_AMOUNT[n]);
        }
        want = best - drop;
        wantId = want > 0 ? FLOWING_LAVA_IDS[Math.min(7, want) - 1] : AIR;
      }
      if (wantId !== id) {
        this.set(x, y, z, wantId);
        if (wantId === AIR) return;
        id = wantId;
      }
    }

    // spread: down first
    const below = h.getBlock(x, y - 1, z);
    if (y > 0 && WATER_AMOUNT[below] > 0) { this.set(x, y - 1, z, STONE_ID); return; }
    if (y > 0 && open(below)) {
      this.set(x, y - 1, z, FALLING_LAVA_ID);
      if (id !== LAVA_ID) return; // vanilla: falling-fed cells don't also spread sideways
    } else {
      const amt = id === FALLING_LAVA_ID ? 8 : LAVA_AMOUNT[id];
      const next = amt - drop;
      if (next > 0) {
        for (let d = 0; d < 4; d++) {
          const nx = x + DX[d], nz = z + DZ[d];
          const n = h.getBlock(nx, y, nz);
          if (open(n)) this.set(nx, y, nz, FLOWING_LAVA_IDS[Math.min(7, next) - 1]);
          else if (IS_LAVA[n] === 1 && n !== LAVA_ID && LAVA_AMOUNT[n] < next && n !== FALLING_LAVA_ID) {
            this.set(nx, y, nz, FLOWING_LAVA_IDS[Math.min(7, next) - 1]);
          }
        }
      }
    }
    this.spreadFire(x, y, z);
  }

  /** Vanilla LavaFluid.randomTick, approximated on updates: light an air cell
   *  up to 2 above / beside the lava next to something flammable. */
  private spreadFire(x: number, y: number, z: number): void {
    const h = this.host;
    if (h.rnd() > 0.3) return;
    const fx = x + Math.floor(h.rnd() * 3) - 1, fy = y + 1 + Math.floor(h.rnd() * 2), fz = z + Math.floor(h.rnd() * 3) - 1;
    if (h.getBlock(fx, fy, fz) !== AIR) return;
    let fuel = false;
    for (const [dx, dy, dz] of [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]]) {
      if (IS_FLAMMABLE[h.getBlock(fx + dx, fy + dy, fz + dz)] === 1) { fuel = true; break; }
    }
    if (!fuel) return;
    this.set(fx, fy, fz, FIRE_ID);
    h.ignite?.(fx, fy, fz);
  }
}
